import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxTgModule, TranslateService } from '@worthy-ventures/metaglotta-ngx';
import { Equipment, EquipmentSpec } from '../../services/equipment.service';
import { EquipmentPayload } from '../../services/admin-equipment.service';

/**
 * Add/edit equipment. Collects the row and returns it via
 * activeModal.close(payload); the caller does the save and the toast, matching
 * how CategoryModalComponent is used.
 *
 * Set `equipment` before opening to edit; leave it null to create.
 */
@Component({
    selector: 'app-equipment-modal',
    imports: [FormsModule, NgxTgModule],
    templateUrl: './equipment-modal.component.html',
    styleUrl: './equipment-modal.component.scss',
})
export class EquipmentModalComponent implements OnInit {
    /** Set by the caller. null = create. */
    equipment: Equipment | null = null;

    code = '';
    slug = '';
    name = '';
    features = '';
    specifications: EquipmentSpec[] = [];

    /** Existing photo (a signed URL from the API) while editing. */
    currentImage: string | null = null;
    /** Data URL of a newly picked file, shown instead of currentImage. */
    newImagePreview: string | null = null;
    newImageFile: File | null = null;
    /** Set when the admin clears the existing photo without picking another. */
    imageCleared = false;
    imageError = '';
    dragging = false;

    /** Rejected before upload — the server enforces the same rules. */
    private static readonly ACCEPTED = ['png', 'jpg', 'jpeg', 'webp', 'gif'];
    private static readonly MAX_BYTES = 5 * 1024 * 1024;

    submitted = false;
    /** Tracks whether the slug was typed by hand, so auto-fill stops interfering. */
    private slugTouched = false;

    constructor(private i18n: TranslateService, public activeModal: NgbActiveModal) { }

    ngOnInit(): void {
        // Reset the picked-file state explicitly. The modal service builds a
        // fresh instance per open, but this keeps re-initialising the same one
        // from leaking a pending upload into the next product.
        this.newImagePreview = null;
        this.newImageFile = null;
        this.imageCleared = false;
        this.imageError = '';

        if (!this.equipment) return;
        this.code = this.equipment.code ?? '';
        this.slug = this.equipment.slug ?? '';
        this.name = this.equipment.name ?? '';
        this.features = this.equipment.features ?? '';
        this.currentImage = this.equipment.image ?? null;
        // copied, not referenced — cancelling must not mutate the list behind us
        this.specifications = (this.equipment.specifications ?? []).map((s) => ({ ...s }));
        this.slugTouched = true;
    }

    get isEdit(): boolean {
        return !!this.equipment;
    }

    /** The slug is the public URL (/equipment/:slug), so changing it breaks links. */
    get slugChanged(): boolean {
        return this.isEdit && this.slug.trim() !== (this.equipment?.slug ?? '');
    }

    /** What the preview shows: a newly picked file wins over the stored photo. */
    get previewUrl(): string | null {
        if (this.newImagePreview) return this.newImagePreview;
        if (this.imageCleared) return null;
        return this.currentImage;
    }

    get imageName(): string {
        return this.newImageFile?.name ?? '';
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.dragging = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.dragging = false;
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        this.dragging = false;
        const file = event.dataTransfer?.files?.[0];
        if (file) this.acceptFile(file);
    }

    onFilePicked(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (file) this.acceptFile(file);
        // reset so re-picking the same file fires change again
        input.value = '';
    }

    /** Validates and reads the file into a data URL for the preview. */
    private acceptFile(file: File): void {
        this.imageError = '';
        const suffix = (file.name.split('.').pop() || '').toLowerCase();
        if (!EquipmentModalComponent.ACCEPTED.includes(suffix)) {
            this.imageError = this.i18n.instant('upload_error_unsupported_type',
                { suffix, accepted: EquipmentModalComponent.ACCEPTED.join(', ') });
            return;
        }
        if (file.size > EquipmentModalComponent.MAX_BYTES) {
            this.imageError = this.i18n.instant('upload_error_too_large',
                { size: (file.size / 1024 / 1024).toFixed(1) });
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            this.newImagePreview = reader.result as string;
            this.newImageFile = file;
            this.imageCleared = false;
        };
        reader.onerror = () => { this.imageError = this.i18n.instant('upload_error_unreadable'); };
        reader.readAsDataURL(file);
    }

    /** Drops the picked file, or marks the stored photo for removal. */
    clearImage(): void {
        this.imageError = '';
        if (this.newImagePreview) {
            this.newImagePreview = null;
            this.newImageFile = null;
            return;
        }
        if (this.currentImage) this.imageCleared = true;
    }

    get codeInvalid(): boolean {
        return this.submitted && this.code.trim() === '';
    }

    get slugInvalid(): boolean {
        return this.submitted && this.slug.trim() === '';
    }

    /**
     * Derives the slug from the code until someone edits the slug directly —
     * "DP-71" becomes "dp-71", which is the shape the old site's URLs used.
     */
    onCodeChange(): void {
        if (this.slugTouched) return;
        this.slug = this.slugify(this.code);
    }

    onSlugInput(): void {
        this.slugTouched = true;
    }

    slugify(value: string): string {
        return (value || '')
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    addSpec(): void {
        this.specifications.push({ label: '', value: '' });
    }

    removeSpec(index: number): void {
        this.specifications.splice(index, 1);
    }

    save(): void {
        this.submitted = true;
        const code = this.code.trim();
        const slug = this.slug.trim();
        if (!code || !slug) return;

        // Blank optional fields are sent as null, not "" — the catalogue pages
        // test truthiness to decide whether to render a section at all.
        const payload: EquipmentPayload = {
            code,
            slug,
            name: this.name.trim() || null,
            features: this.features.trim() || null,
            // drop half-filled rows rather than storing empty labels
            specifications: this.specifications
                .map((s) => ({ label: (s.label || '').trim(), value: (s.value || '').trim() }))
                .filter((s) => s.label !== '' || s.value !== ''),
        };

        if (this.newImagePreview && this.newImageFile) {
            payload.imageBase64 = this.newImagePreview;
            payload.imageFilename = this.newImageFile.name;
            payload.imageContentType = this.newImageFile.type;
        } else if (this.imageCleared) {
            payload.removeImage = true;
        }
        // Neither set: the request carries no image instruction and the server
        // leaves the existing photo untouched.

        this.activeModal.close(payload);
    }
}
